import { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toggleChangemakerLike } from '../api/api';
import { track } from '../utils/analytics';

const PENDING_LIKE_KEY = 'karmana_pending_like_changemaker';

/**
 * Reusable heart/follow button for organisations.
 * Shows "Followed [Org Name]" toast for 4 seconds on follow.
 * Pass `trigger` prop to name the analytics event.
 * Pass `orgName` prop for the follow toast message.
 */
const HeartButton = ({ changemakerId, orgName = '', trigger = 'changemaker_card_liked', size = 22, className = '' }) => {
  const { user, openAuthModal } = useAuth();

  const isLiked = () => (user?.liked_changemakers || []).includes(changemakerId);

  const [liked, setLiked] = useState(isLiked);
  const [toast, setToast] = useState(false);
  const [toastMsg, setToastMsg] = useState('');

  // Sync when user object changes (e.g. after auth)
  useEffect(() => {
    setLiked(isLiked());
  }, [user, changemakerId]);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      track('signup_trigger', { trigger, page: window.location.pathname.includes('/organizations/') ? 'organisation_detail' : 'organisations', changemaker_id: changemakerId });
      localStorage.setItem(
        PENDING_LIKE_KEY,
        JSON.stringify({ changemakerId, returnPath: window.location.pathname })
      );
      openAuthModal('changemaker_like');
      return;
    }

    const newLiked = !liked;
    setLiked(newLiked);
    track('org_like_click', { organisation_id: changemakerId, action: newLiked ? 'liked' : 'unliked', trigger, source_page: window.location.pathname.includes('/organizations/') ? 'organisation_detail' : 'organisations' });

    if (newLiked) {
      const name = orgName || 'this organisation';
      setToastMsg(`Followed ${name}`);
      setToast(true);
      setTimeout(() => setToast(false), 4000);
    }

    toggleChangemakerLike(changemakerId).then((res) => {
      setLiked(res.data.liked);
    }).catch(() => {
      setLiked(!newLiked);
    });
  };

  return (
    <>
      <button
        onClick={handleClick}
        className={`focus:outline-none transition-transform hover:scale-110 active:scale-95 ${className}`}
        data-testid={`heart-btn-${changemakerId}`}
        aria-label={liked ? 'Unfollow organisation' : 'Follow organisation'}
        title={liked ? 'Following' : 'Follow'}
      >
        <Heart
          size={size}
          strokeWidth={2}
          className={liked ? 'fill-green-500 text-green-500' : 'text-green-500'}
        />
      </button>

      {toast && (
        <div
          className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[300] bg-gray-900 text-white text-sm px-5 py-3 rounded-full shadow-xl pointer-events-none"
          style={{ animation: 'fadeInUp 0.25s ease' }}
          data-testid="like-toast"
        >
          {toastMsg}
        </div>
      )}
    </>
  );
};

export default HeartButton;
